import { Button } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/formatMoney';
import { ICashMeSimulationResult } from '../../types';
import { WizardShell } from '../WizardShell/WizardShell';
import wizardStyles from '../WizardShell/WizardShell.module.css';
import styles from './SimulationResult.module.css';

interface ISimulationResult {
  simulation: ICashMeSimulationResult;
  interestTable: string;
  installmentPeriod: number;
  idealIncomeCommitmentPercent?: number;
  onContinue: () => void;
  onBack: () => void;
}

/* Rates come back as decimal fractions: 0.185 is 18,5%. */
const percent = (rate?: number) =>
  rate === undefined ? null : `${(rate * 100).toFixed(2).replace('.', ',')}%`;

export function SimulationResult({
  simulation,
  interestTable,
  installmentPeriod,
  idealIncomeCommitmentPercent,
  onContinue,
  onBack,
}: ISimulationResult) {
  /* Every field but actionLeadId and simulationId may be absent. */
  const cells = [
    ['Primeira parcela', formatMoney(simulation.firstPayment)],
    ['Última parcela', formatMoney(simulation.lastPayment)],
    [
      'Taxa de juros',
      percent(simulation.interestPerMonth)
        ? `${percent(simulation.interestPerMonth)} a.m.`
        : null,
    ],
    [
      'Taxa de juros',
      percent(simulation.interestPerYear)
        ? `${percent(simulation.interestPerYear)} a.a.`
        : null,
    ],
    [
      'CET',
      percent(simulation.cetPerMonth)
        ? `${percent(simulation.cetPerMonth)} a.m.`
        : null,
    ],
    [
      'CET',
      percent(simulation.cetPerYear)
        ? `${percent(simulation.cetPerYear)} a.a.`
        : null,
    ],
    ['Saldo devedor', formatMoney(simulation.balanceDue)],
    ['Renda necessária', formatMoney(simulation.necessaryIncome)],
  ].filter(([, value]) => Boolean(value)) as [string, string][];

  return (
    <WizardShell
      title="Confira o resultado da simulação"
      helper={`Tabela ${interestTable} em ${installmentPeriod} meses. Se as condições fizerem sentido, siga para solicitar o crédito.`}
    >
      <div className={styles.grid}>
        {cells.map(([label, value], index) => (
          <div className={styles.cell} key={`${label}-${index}`}>
            <span className={styles.cellLabel}>{label}</span>
            <span className={styles.cellValue}>{value}</span>
          </div>
        ))}
      </div>

      {simulation.necessaryIncome !== undefined && (
        <div className={styles.warning}>
          <i className="material-icons-outlined">info</i>
          <p>
            {idealIncomeCommitmentPercent
              ? `O ideal é que a parcela comprometa até ${idealIncomeCommitmentPercent}% da renda. `
              : ''}
            Para este valor, a renda mensal indicada é de{' '}
            <strong>{formatMoney(simulation.necessaryIncome)}</strong>. Se a sua
            renda for menor, a proposta ainda pode ser enviada, mas a CashMe
            pode apontar isso na análise.
          </p>
        </div>
      )}

      <p className={styles.disclaimer}>
        Esses valores são uma simulação com base em uma taxa média. Por isso,
        estão sujeitos a mudanças após a análise de crédito pela equipe CashMe.
        Consulte sempre as condições e o Custo Efetivo Total (CET), seguros e
        IOF antes da contratação.
      </p>

      <div className={wizardStyles.footer}>
        <Button type="button" color="flat" text="Voltar" onClick={onBack} />
        <Button
          type="button"
          color="primary"
          text="Solicitar crédito"
          onClick={onContinue}
        />
      </div>
    </WizardShell>
  );
}
